import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import MappingLeftSideHeader from "./MappingLeftSideHeader";
import TakeActionCards from "./TakeActionCards";
import ActivityCard from "./ActivityCard";
import {
  useDeleteFollowUpReminder,
  useDeleteRelationshipRecordedNote,
  useFetchFollowUpReminder,
  useGetRelationshipRecordedNotes,
  useRelationshipMappingDetail,
  useRefreshRelationshipMappingDetail,
  useSaveRelationshipContactDetails,
} from "../../hooks/useAccountRelationship";
import { getCleanPlainText } from "@/shared/utils/accountConfig";
import {
  // Activity,
  Briefcase,
  Check,
  Copy,
  FileText,
  Megaphone,
  MessageSquareText,
  Pencil,
  Trash,
} from "lucide-react";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import FollowUpCard from "./FollowUpCard";
import CampaignActivityList from "./CampaignActivityList";
import { markdownToHtml } from "@/shared/utils/markdownUtils";
import Loader from "@/shared/components/ui/Loaders/Loader";
import BorderCard from "@/features/campaigns/components/UserCampaigns/components/BorderCard";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import PeopleInContactBlock from "./PeopleInContactBlock";
import { showError } from "@/shared/utils/toast";
import Quote from "@/assets/svg/Quote";
import CallButton from "@/features/voice/components/CallButton";
// import { StatusChip } from "@/shared/components/ui/Status/StatusChip";

const MappingLeftSideArea = ({
  selectedMappingContact,
  setShowDraftModal,
  actionsType,
  setIsDrawerOpen,
  setDrawerType,
  setNoteToEdit,
  setFollowUpToEdit,
}) => {
  const relationshipMappingId = selectedMappingContact?.relationship_mapping_id;
  const queryClient = useQueryClient();

  const {
    data: relationshipMappingDetail,
    isLoading: isRelationshipMappingDetailLoading,
  } = useRelationshipMappingDetail({
    relationshipMappingId,
  });
  const {
    mutate: refreshRelationshipMappingDetail,
    isPending: isRefreshingRelationshipMappingDetail,
  } = useRefreshRelationshipMappingDetail();

  // The detail call can correct the title or mark it unverified; refresh the list to match.
  const hasDetail = Boolean(relationshipMappingDetail);
  const detailTitle = relationshipMappingDetail?.designation;
  const detailTitleVerified = relationshipMappingDetail?.title_verified ?? null;
  const listTitle = selectedMappingContact?.designation || "";
  const listTitleVerified = selectedMappingContact?.title_verified ?? null;
  useEffect(() => {
    if (!hasDetail) return;
    if (detailTitle !== listTitle || detailTitleVerified !== listTitleVerified) {
      queryClient.invalidateQueries({ queryKey: ["relationshipMapping"] });
    }
  }, [
    hasDetail,
    detailTitle,
    detailTitleVerified,
    listTitle,
    listTitleVerified,
    queryClient,
  ]);
  const { mutateAsync: saveContactDetails } =
    useSaveRelationshipContactDetails();

  const {
    data: relationshipRecordedNotes,
    isLoading: isRelationshipRecordedNotesLoading,
  } = useGetRelationshipRecordedNotes({
    relationshipMappingId,
  });

  const { data: followUpReminder, isLoading: isFollowUpReminderLoading } =
    useFetchFollowUpReminder({
      relationshipMappingId,
    });

  const [noteToDelete, setNoteToDelete] = useState(null);
  const [followUpToDelete, setFollowUpToDelete] = useState(null);

  const { mutate: deleteNote, isPending: isDeletingNote } =
    useDeleteRelationshipRecordedNote({
      action: () => setNoteToDelete(null),
    });

  const { mutate: deleteFollowUp, isPending: isDeletingFollowUp } =
    useDeleteFollowUpReminder({
      action: () => setFollowUpToDelete(null),
    });

  const noteActions = useMemo(
    () => [
      {
        id: "edit",
        name: "Edit",
        icon: <Pencil size={16} />,
        onClick: (noteId) => {
          const note = relationshipRecordedNotes?.notes?.find(
            (item) => item.id === noteId,
          );
          if (!note) return;

          setNoteToEdit(note);
          setDrawerType("notes");
          setIsDrawerOpen(true);
        },
      },
      {
        id: "delete",
        name: "Delete",
        icon: <Trash size={16} />,
        onClick: (noteId) => setNoteToDelete(noteId),
      },
    ],
    [
      relationshipRecordedNotes?.notes,
      setDrawerType,
      setIsDrawerOpen,
      setNoteToEdit,
    ],
  );

  const followUpActions = useMemo(
    () => [
      {
        id: "edit",
        name: "Edit",
        icon: <Pencil size={16} />,
        onClick: (followUpId) => {
          const followUp = followUpReminder?.follow_ups?.find(
            (item) => item.id === followUpId,
          );
          if (!followUp) return;

          setFollowUpToEdit(followUp);
          setDrawerType("followUpReminder");
          setIsDrawerOpen(true);
        },
      },
      {
        id: "delete",
        name: "Delete",
        icon: <Trash size={16} />,
        onClick: (followUpId) => setFollowUpToDelete(followUpId),
      },
    ],
    [
      followUpReminder?.follow_ups,
      setDrawerType,
      setFollowUpToEdit,
      setIsDrawerOpen,
    ],
  );

  const hasRoleSummary =
    relationshipMappingDetail?.professional_snapshot?.length > 0;
  const hasPeopleInContact =
    relationshipMappingDetail?.people_in_contact?.length > 0;
  const hasPitchOrObjections =
    relationshipMappingDetail?.what_to_pitch ||
    relationshipMappingDetail?.risk_objection_signals?.length > 0;
  const hasWhyThisContactMatters =
    relationshipMappingDetail?.why_this_contact_matters;
  const hasTalkTrack = relationshipMappingDetail?.talk_track;
  const hasSignals =
    relationshipMappingDetail?.risk_objection_signals?.length > 0;

  const isAnyContentEmpty = useMemo(() => {
    if (!relationshipMappingDetail) return false;
    const professionalSnapshot = relationshipMappingDetail.professional_snapshot;
    const whyThisContactMatters = relationshipMappingDetail.why_this_contact_matters;
    const whatToPitch = relationshipMappingDetail.what_to_pitch;
    const talkTrack = relationshipMappingDetail.talk_track;

    const isSnapshotEmpty = !professionalSnapshot || professionalSnapshot.length === 0;
    const isWhyMattersEmpty = !whyThisContactMatters || (typeof whyThisContactMatters === "string" && whyThisContactMatters.trim() === "");
    const isPitchEmpty = !whatToPitch || (typeof whatToPitch === "string" && whatToPitch.trim() === "");
    const isTalkTrackEmpty = !talkTrack || (typeof talkTrack === "string" && talkTrack.trim() === "");

    return isSnapshotEmpty || isWhyMattersEmpty || isPitchEmpty || isTalkTrackEmpty;
  }, [relationshipMappingDetail]);

  const [scriptCopied, setScriptCopied] = useState(false);

  useEffect(() => {
    setScriptCopied(false);
  }, [relationshipMappingId]);

  const handleCopyScript = async () => {
    const text = getCleanPlainText(relationshipMappingDetail?.talk_track || "");
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setScriptCopied(true);
      window.setTimeout(() => setScriptCopied(false), 2000);
    } catch {
      showError("Failed to copy script.");
    }
  };

  const handleCallFinished = useCallback(
    ({ status }) => {
      if (status !== "ended") return;
      queryClient.setQueryData(
        ["relationshipMappingDetail", relationshipMappingId],
        (current) => current ? { ...current, last_call_at: new Date().toISOString() } : current,
      );
    },
    [queryClient, relationshipMappingId],
  );

  const formatContactTime = (value) =>
    value
      ? new Intl.DateTimeFormat(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(value))
      : null;

  const isContactDetailsLoading =
    !!relationshipMappingId &&
    ((isRelationshipMappingDetailLoading && !relationshipMappingDetail) ||
      (isRelationshipRecordedNotesLoading &&
        relationshipRecordedNotes == null) ||
      (isFollowUpReminderLoading && followUpReminder == null));

  if (!relationshipMappingId) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] w-full">
        <p className="text-xs text-gray-500 font-[Inter]">
          Select a contact to view details.
        </p>
      </div>
    );
  }

  if (isContactDetailsLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] w-full">
        <Loader text="Loading contact details..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* profile info */}
      <MappingLeftSideHeader
        relationshipMappingDetail={relationshipMappingDetail}
        isRefreshing={isRefreshingRelationshipMappingDetail}
        onRefresh={() =>
          refreshRelationshipMappingDetail({ relationshipMappingId })
        }
        showRefreshButton={isAnyContentEmpty}
      />

      {/* live signals */}

      {/* role summary */}
      {hasRoleSummary && (
        <div className="flex flex-col">
          <BorderCard variant="top" padding="p-4">
            <div className="flex items-center gap-3">
              <Briefcase className="text-secondary w-5 h-4" />
              <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                Role Summary
              </h5>
            </div>
          </BorderCard>
          <BorderCard variant="bottom" className="p-4">
            <div className="flex items-start gap-8 w-full">
              <ul className="flex w-[48%] list-outside list-disc flex-col gap-4 pl-4 text-xs font-normal text-gray-800 dark:text-gray-200 marker:text-black dark:marker:text-white">
                {relationshipMappingDetail?.professional_snapshot?.map(
                  (item) => (
                    <li key={item}>{getCleanPlainText(item)}</li>
                  ),
                )}
              </ul>

              <div className="flex flex-col gap-4 w-[52%]">
                {/* why this contact matters */}
                {hasWhyThisContactMatters && (
                  <div className="p-4 rounded-2xl border-[1.5px] border-[#DFF2F7] dark:border-secondary/30 flex flex-col gap-1 bg-secondary-light-transparent dark:bg-secondary/10">
                    {/* title */}
                    <h5 className="text-xs font-semibold text-secondary dark:text-secondary-light font-[Inter]">
                      WHY THIS ROLE MATTERS FOR YOU
                    </h5>
                    <p
                      className=" text-xs font-normal text-gray-800 dark:text-gray-200 font-[Inter]"
                      dangerouslySetInnerHTML={{
                        __html: markdownToHtml(
                          getCleanPlainText(
                            relationshipMappingDetail?.why_this_contact_matters,
                          ),
                        ),
                      }}
                    />
                  </div>
                )}
                {hasPeopleInContact && (
                  <PeopleInContactBlock
                    contactName={relationshipMappingDetail?.contact_name}
                    people={relationshipMappingDetail?.people_in_contact}
                  />
                )}
              </div>
            </div>
          </BorderCard>
        </div>
      )}

      {/* what to pitch and objection points */}
      <div className={`flex gap-4 w-full`}>
        {hasPitchOrObjections && (
          <div className={`flex flex-col ${hasSignals ? "w-1/2" : "w-full"}`}>
            <BorderCard variant="top" padding="p-4">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Megaphone className="text-secondary w-5 h-4" />
                  <div className="flex flex-col gap-1">
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                      What to Pitch
                    </h5>
                    <p className="text-[10px] font-normal text-gray-500 dark:text-gray-400">
                      Angles tuned to her role
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <SecondaryButton
                    title="Draft Email"
                    className="text-[10.5px] font-semibold text-gray-900 dark:text-gray-100"
                    onClick={() => setShowDraftModal(true)}
                  />
                  <span className="text-[9px] text-gray-500 dark:text-gray-400">
                    {relationshipMappingDetail?.last_email_sent_at
                      ? `Last emailed ${formatContactTime(relationshipMappingDetail.last_email_sent_at)}`
                      : "No direct email sent"}
                  </span>
                </div>
              </div>
            </BorderCard>
            <BorderCard variant="bottom" padding="p-4">
              <p
                className="text-xs font-normal text-gray-800 dark:text-gray-200 font-[Inter]"
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(
                    getCleanPlainText(relationshipMappingDetail?.what_to_pitch),
                  ),
                }}
              />
            </BorderCard>
          </div>
        )}
      </div>

      {/* talk track */}
      {hasTalkTrack && (
        <div className="flex flex-col">
          <BorderCard variant="top" padding="p-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <MessageSquareText className="text-secondary w-5 h-4" />
                <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Talk Track
                </h5>
              </div>
              <div className="flex flex-col items-end gap-1">
                <CallButton
                  phoneNumber={relationshipMappingDetail?.crm_contact?.whatsapp_number}
                  relationContactId={relationshipMappingId}
                  contactName={relationshipMappingDetail?.contact_name}
                  label={`Call ${relationshipMappingDetail?.contact_name || "contact"}`}
                  variant="secondary"
                  className="text-[10.5px] font-semibold text-gray-900 dark:text-gray-100"
                  onSavePhone={(whatsappNumber) =>
                    saveContactDetails({
                      relationshipMappingId,
                      whatsappNumber,
                    })
                  }
                  onCallFinished={handleCallFinished}
                />
                <span className="text-[9px] text-gray-500 dark:text-gray-400">
                  {relationshipMappingDetail?.last_call_at
                    ? `Last called ${formatContactTime(relationshipMappingDetail.last_call_at)}`
                    : "No call made yet"}
                </span>
              </div>
            </div>
          </BorderCard>
          <BorderCard variant="bottom" padding="px-4 py-5">
            <div className="flex gap-2 items-start w-full">
              <div className="w-fit text-secondary dark:text-secondary-light">
                <Quote />
              </div>
              <div className="flex flex-col gap-3">
                <div className="text-xs font-normal text-gray-800 dark:text-gray-200 font-[Inter] [&_p]:text-gray-800 dark:[&_p]:text-gray-200">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: markdownToHtml(
                        `“${relationshipMappingDetail?.talk_track}”`,
                      ),
                    }}
                  />
                </div>

                <SecondaryButton
                  title={scriptCopied ? "Copied" : "Copy Script"}
                  icon={
                    scriptCopied ? (
                      <Check
                        size={16}
                        className="text-success-500"
                        strokeWidth={2}
                      />
                    ) : (
                      <Copy
                        size={16}
                        className="text-secondary"
                        strokeWidth={2}
                      />
                    )
                  }
                  className={`text-[10.5px] font-semibold w-fit px-0.5 py-1 border-none shadow-none ${scriptCopied ? "text-success-600 dark:text-success-400" : "text-secondary"
                    }`}
                  onClick={handleCopyScript}
                  textColor="light"
                  disabled={scriptCopied}
                />
              </div>
            </div>
          </BorderCard>
        </div>
      )}

      {/* Take action */}

      <div>
        <BorderCard variant="top" padding="p-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <FileText className="text-secondary w-5 h-4" />
              <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                Take Actions
              </h5>
            </div>
            {/* action button to call the contact */}
            {/* <div className="flex justify-end">
                <SecondaryButton
                  title={`Call ${getCleanName(relationshipMappingDetail?.contact_name)}`}
                  icon={<PhoneCall size={16} className="text-gray-500" />}
                  className="text-[10.5px] font-semibold text-gray-900"
                  // onClick={() => {
                  //   setShowDraftModal(true);
                  // }}
                />
              </div> */}
          </div>
        </BorderCard>
        <BorderCard variant="bottom" padding="px-4 py-5">
          <div className="grid md:grid-cols-3 grid-cols-1 gap-2.5 w-full">
            {actionsType?.map((item) => (
              <TakeActionCards key={item?.id} item={item} />
            ))}
          </div>
        </BorderCard>
      </div>

      {/* activity card */}
      <div className="grid md:grid-cols-3 grid-cols-1 gap-2.5 w-full">
        {/* recorded notes */}
        <ActivityCard title="Recorded Notes">
          {relationshipRecordedNotes?.notes?.length > 0 ? (
            <ul className="w-full list-outside list-disc pl-4 text-xs font-normal text-gray-800 dark:text-gray-200 marker:text-black dark:marker:text-white">
              {relationshipRecordedNotes?.notes?.map((note, index) => (
                <div
                  className="flex items-center gap-2 justify-between w-full"
                  key={note?.id}
                >
                  <li
                    className={`text-gray-500 dark:text-gray-300 font-normal text-xs ${index === relationshipRecordedNotes?.notes?.length - 1 ? "pb-0" : "pb-1.5"}`}
                    key={note?.id}
                  >
                    {getCleanPlainText(note?.body)}
                  </li>
                  <div className=" ml-auto self-center">
                    <CommonDropdownMenu
                      actions={noteActions}
                      itemId={note?.id}
                      iconSize={12}
                    />
                  </div>
                </div>
              ))}
            </ul>
          ) : (
            <p className="text-xs font-normal text-gray-500 dark:text-gray-400 font-[Inter] text-center items-center justify-center flex h-full">
              No recorded notes found. Take a note of the interaction and notes.
            </p>
          )}
        </ActivityCard>

        {/* Campaign Activity */}
        <ActivityCard title="Campaign Activity">
          <CampaignActivityList
            campaignActivities={relationshipMappingDetail?.campaignActivities}
            contactName={relationshipMappingDetail?.contact_name}
          />
        </ActivityCard>

        {/* Follow-up Reminder */}
        <ActivityCard title="Follow-ups and Activity">
          {followUpReminder?.follow_ups?.length > 0 ? (
            <div className="flex flex-col gap-4 pb-1">
              {followUpReminder.follow_ups.map((followUp) => (
                <FollowUpCard
                  key={followUp.id}
                  followUp={followUp}
                  companyName={relationshipMappingDetail?.contact_name}
                  actions={followUpActions}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs font-normal text-gray-500 dark:text-gray-400 font-[Inter] text-center items-center justify-center flex h-full">
              No follow-up reminders found. Schedule your next interaction.
            </p>
          )}
        </ActivityCard>
      </div>

      {noteToDelete && (
        <DeleteConfirmationModal
          showConfirmDelete={!!noteToDelete}
          onClose={() => setNoteToDelete(null)}
          title="Delete this note?"
          description="This note will be permanently removed from recorded notes."
          onDelete={() =>
            deleteNote({
              noteId: noteToDelete,
              relationshipMappingId,
            })
          }
          isDeleting={isDeletingNote}
          deleteButtonText="Delete Note"
        />
      )}

      {followUpToDelete && (
        <DeleteConfirmationModal
          showConfirmDelete={!!followUpToDelete}
          onClose={() => setFollowUpToDelete(null)}
          title="Delete this follow-up reminder?"
          description="This reminder will be permanently removed."
          onDelete={() =>
            deleteFollowUp({
              followUpId: followUpToDelete,
              relationshipMappingId,
            })
          }
          isDeleting={isDeletingFollowUp}
          deleteButtonText="Delete Reminder"
        />
      )}
    </div>
  );
};

export default MappingLeftSideArea;
